/**
 * groundedMotion - the pure math behind the grounded character controller
 * (src/input/groundController.js).
 *
 * Babylon's UniversalCamera "gravity" is a raw per-frame displacement added
 * straight into the collide-and-slide displacement (FreeCamera._collideWithWorld
 * adds scene.gravity, default -9.807, to a walk step of ~0.2). On flat ground
 * the slide projection annihilates it, so walking looks correct; on a slope it
 * survives as g*sin(theta) pointing DOWNHILL. Measured: touching the editor's
 * default ramp threw the camera ~6.9 units back down it in ONE frame, so
 * players bounced off its foot forever instead of climbing.
 *
 * So the app turns applyGravity off and owns the vertical axis: probe the floor
 * once per frame, snap to it while grounded, and integrate real delta-scaled
 * gravity while not. This module is the decision half - resolveGroundState()
 * takes a probe result plus the current motion and returns the next vertical
 * state. Everything here is in ELLIPSOID-CENTER space (the collision origin,
 * camera.position.y - camera.ellipsoid.y), not eye space.
 *
 * Pure, framework-free module: no DOM, no Babylon.
 */

const DEG_TO_RAD = Math.PI / 180;

/**
 * @typedef {Object} GroundOptions
 * @property {number} maxSlopeDeg
 * @property {number} gravity
 * @property {number} terminalVelocity
 * @property {number} snapDownCap
 * @property {number} snapDownSkin
 * @property {number} snapUpMax
 * @property {number} groundedSkin
 * @property {number} climbSkin
 * @property {number} probeUp
 * @property {number} probeLength
 * @property {number} resyncProbeLength
 * @property {number} maxStepDt
 */

/**
 * Tuning for the grounded controller. Distances are world units, velocities
 * units/second. Exported so tests and callers express intent instead of magic
 * numbers (same convention as ARRIVE_RADIUS in walkNav.js).
 * @type {Readonly<GroundOptions>}
 */
export const GROUND_DEFAULTS = Object.freeze({
  /**
   * Steepest surface you can stand on. The editor's default ramp is 26.6
   * degrees (rise 4 over run 8); 50 leaves room for a deliberately steep
   * authored ramp without letting anyone creep up a near-vertical face.
   */
  maxSlopeDeg: 50,
  /**
   * Downward acceleration while airborne. The world is roughly 2x human scale
   * (eye height 2.0 units), so real-world 9.807 reads as floaty here. Only
   * observable when falling off the edge of a level.
   */
  gravity: 24,
  /** Fall speed cap - reaches the y < -50 respawn in movement.js in ~1.7s. */
  terminalVelocity: 45,
  /**
   * Hard ceiling on how far below rest height we still stick to the floor in
   * one tick. camera.speed can reach 1.0/frame (touch 0.5 x the max
   * moveSpeedMultiplier of 2), so the proportional term alone can get large.
   */
  snapDownCap: 1.5,
  /** Slack added to the proportional stick distance. */
  snapDownSkin: 0.1,
  /**
   * Cap on a single upward correction, so geometry appearing underfoot nudges
   * the player up over a few frames instead of teleporting them.
   */
  snapUpMax: 0.6,
  /** How far the center may sink below rest height before we push it back up. */
  groundedSkin: 0.05,
  /**
   * Float slack on the climb-rate slope test. Deliberately tiny: the test is
   * exact (collide-and-slide yields climb/step === tan(slope) to many digits),
   * and every unit of slack here is free vertical climb per FRAME - 0.05 would
   * hand out 3 units/second of wall scaling.
   */
  climbSkin: 0.002,
  /** Probe origin sits this far above the center, so a slightly penetrated
   *  center still starts its ray outside the geometry. */
  probeUp: 0.25,
  /**
   * Probe length. Must clear probeUp + the rest offset at the slope limit
   * (2.05 for a (1.5, 1) ellipsoid at 50 degrees) + snapDownCap.
   */
  probeLength: 6,
  /** Teleports re-ground across a whole level in one tick. */
  resyncProbeLength: 300,
  /** Anti-tunnel clamp: one long frame must not integrate a huge fall. */
  maxStepDt: 1 / 20,
});

/**
 * Overrides that let resolveGroundState() ground the camera from any height or
 * depth in a single tick. Used only after a teleport (spawn, respawn, presenter
 * gather, drone exit), where the incremental snap limits would otherwise leave
 * the player hanging in the air or buried. Never applied on a normal frame.
 * @type {Partial<GroundOptions>}
 */
export const RESYNC_OVERRIDES = Object.freeze({
  snapDownCap: Number.POSITIVE_INFINITY,
  snapDownSkin: Number.POSITIVE_INFINITY,
  snapUpMax: Number.POSITIVE_INFINITY,
});

/**
 * @typedef {Object} GroundProbe
 * @property {boolean} hit whether the downward ray found a surface
 * @property {number} surfaceY world Y of the hit point
 * @property {number} normalY upward component of the surface normal (0..1)
 * @property {number} normalXZ horizontal magnitude of the normal, hypot(nx, nz)
 */

/**
 * @typedef {Object} Motion
 * @property {number} centerY current ellipsoid-center height
 * @property {number} vy vertical velocity in units/second
 * @property {number} dt seconds elapsed since the previous tick
 * @property {number} horizontalStep XZ distance covered since the previous tick
 * @property {number} [climb] vertical distance collide-and-slide produced since
 *   the previous tick (positive = the collider rode up something)
 * @property {number} [previousRest] last tick's rest offset, so leaving a slope
 *   for flat ground is not mistaken for stepping off a ledge
 */

/**
 * @typedef {Object} Radii
 * @property {number} xz horizontal ellipsoid radius (camera.ellipsoid.x)
 * @property {number} y vertical ellipsoid radius (camera.ellipsoid.y)
 */

/**
 * @typedef {Object} GroundState
 * @property {boolean} grounded standing on a walkable surface
 * @property {number} centerY resolved ellipsoid-center height
 * @property {number} vy resolved vertical velocity (0 while grounded)
 * @property {'climbing'|'snapped'|'landed'|'falling'|'steep'|'void'} phase why
 * @property {number | null} rest rest offset of the surface under the center,
 *   null when there is nothing walkable there; feed back as Motion.previousRest
 */

/**
 * Cosine of the slope limit - the form isWalkableNormal() compares against,
 * since a surface normal's Y component IS the cosine of its slope.
 * @param {number} maxSlopeDeg
 * @returns {number}
 */
export function slopeLimitCos(maxSlopeDeg) {
  return Math.cos(maxSlopeDeg * DEG_TO_RAD);
}

/**
 * Vertical distance from the ellipsoid CENTER down to the surface point
 * directly beneath it, when the ellipsoid rests tangent on that surface.
 *
 * This is NOT simply radii.y once the surface tilts: the support distance from
 * the center to a tangent plane with unit normal n is
 * sqrt(rx^2*nx^2 + ry^2*ny^2 + rz^2*nz^2), and the surface point straight below
 * is further away than the tangent point by a factor 1/ny. Snapping a
 * (1.5, 1, 1.5) ellipsoid to surfaceY + 1.0 on a 26.6 degree ramp would bury it
 * 0.25 units into the wedge and fight the collider; the true offset is 1.25.
 *
 * @param {Radii} radii
 * @param {number} normalY upward component of the surface normal
 * @param {number} normalXZ horizontal magnitude of the surface normal
 * @returns {number} the vertical offset, or Infinity for a vertical face
 */
export function ellipsoidRestOffset(radii, normalY, normalXZ) {
  const ny = Math.abs(normalY);
  if (!Number.isFinite(ny) || ny <= 1e-6) return Number.POSITIVE_INFINITY;
  return Math.hypot(radii.xz * Math.abs(normalXZ), radii.y * ny) / ny;
}

/**
 * Is a surface with this normal shallow enough to stand on?
 * @param {number} normalY upward component of the surface normal
 * @param {number} maxSlopeCos from slopeLimitCos()
 * @returns {boolean}
 */
export function isWalkableNormal(normalY, maxSlopeCos) {
  if (!Number.isFinite(normalY)) return false;
  // 1e-9 so a normal sitting exactly on the limit reads as walkable despite
  // float error in the caller's cosine.
  return Math.abs(normalY) + 1e-9 >= maxSlopeCos;
}

/**
 * How far below rest height the controller still follows the floor this tick.
 *
 * Proportional to the distance actually travelled, because that is exactly how
 * much a maxSlope descent can drop in one tick. Keeping it tied to the measured
 * step (rather than a constant) is what makes ground-following behave the same
 * at 15fps and 120fps even though the horizontal move is per-frame.
 *
 * @param {number} horizontalStep XZ distance covered since the previous tick
 * @param {Partial<GroundOptions>} [options]
 * @returns {number}
 */
export function snapDownLimit(horizontalStep, options = {}) {
  const o = { ...GROUND_DEFAULTS, ...options };
  const step = Number.isFinite(horizontalStep) ? Math.max(0, horizontalStep) : 0;
  const drop = step * Math.tan(o.maxSlopeDeg * DEG_TO_RAD) + o.snapDownSkin;
  return Math.min(o.snapDownCap, drop);
}

/**
 * Flat walk basis from a camera yaw, in Babylon's convention (yaw 0 faces +Z,
 * yaw PI/2 faces +X).
 *
 * Deliberately derived from yaw rather than flattening the camera's forward
 * vector: it stays well defined at +/-90 degrees of pitch, where a
 * flatten-then-normalize would divide by ~0. Walking must be pitch-independent
 * now that the app owns gravity - a pitch-carrying forward vector would let you
 * levitate by looking up and holding W.
 *
 * @param {number} yaw camera.rotation.y
 * @returns {{x: number, y: number, z: number}} unit vector in the XZ plane
 */
export function walkForward(yaw) {
  return { x: Math.sin(yaw), y: 0, z: Math.cos(yaw) };
}

/**
 * Resolve the next vertical state from a floor probe and the current motion.
 *
 * @param {GroundProbe | null} probe result of this tick's downward ray
 * @param {Motion} motion
 * @param {Radii} radii the camera's collision ellipsoid radii
 * @param {Partial<GroundOptions>} [options] overrides (resync passes Infinity
 *   for the snap limits so a teleport grounds in one tick)
 * @returns {GroundState}
 */
export function resolveGroundState(probe, motion, radii, options = {}) {
  const o = { ...GROUND_DEFAULTS, ...options };
  const dt = Number.isFinite(motion.dt) ? Math.max(0, Math.min(o.maxStepDt, motion.dt)) : 0;
  const vy0 = Number.isFinite(motion.vy) ? motion.vy : 0;

  // Rest offset for whatever the probe found, or null when there is nothing
  // walkable beneath us. Computed up front so every branch can report it -
  // the next tick needs it to tell a slope flattening out from a ledge.
  const walkable =
    probe &&
    probe.hit &&
    Number.isFinite(probe.surfaceY) &&
    isWalkableNormal(probe.normalY, slopeLimitCos(o.maxSlopeDeg));
  const restRaw = walkable ? ellipsoidRestOffset(radii, probe.normalY, probe.normalXZ) : NaN;
  const rest = Number.isFinite(restRaw) ? restRaw : null;

  /**
   * @param {'falling'|'steep'|'void'} phase
   * @returns {GroundState}
   */
  const fall = (phase) => {
    const vy = Math.max(-o.terminalVelocity, vy0 - o.gravity * dt);
    return { grounded: false, centerY: motion.centerY + vy * dt, vy, phase, rest };
  };

  // THE CLIMB: collide-and-slide rode us up something this tick. Keep it.
  //
  // The probe is a single ray down from the ellipsoid CENTER, but the ellipsoid
  // is 1.5 units wide - approaching a ramp, its leading rim is already on the
  // slope while its center is still over flat floor. Snapping to what the
  // center sees would undo the lift every frame and wall the player off at the
  // ramp's toe, so while the collider is lifting us the probe does not get a
  // vote. It regains one the moment the lift stops, which is what makes descent
  // and contact work.
  //
  // The slope limit is enforced on the OBSERVED climb rate rather than a
  // surface normal, which is what stops the same rule letting anyone ride up a
  // near-vertical face: rise per unit of horizontal travel must stay within
  // tan(maxSlope). Pushing into a wall (no horizontal travel) allows nothing.
  const climb = Number.isFinite(motion.climb) ? motion.climb : 0;
  if (climb > 1e-6) {
    const allowed =
      Math.max(0, motion.horizontalStep || 0) * Math.tan(o.maxSlopeDeg * DEG_TO_RAD) + o.climbSkin;
    if (climb <= allowed) {
      return { grounded: true, centerY: motion.centerY, vy: 0, phase: 'climbing', rest };
    }
    // Too steep to have been a walk: give the lift back and carry on as if the
    // collider had held us in place.
    motion = { ...motion, centerY: motion.centerY - climb };
  }

  if (!probe || !probe.hit || !Number.isFinite(probe.surfaceY)) return fall('void');
  if (rest === null) return fall('steep');

  const restY = probe.surfaceY + rest;
  const gap = motion.centerY - probe.surfaceY;

  // Sunk into the floor (geometry rose underfoot, or a fall overshot): push
  // back up, but never by more than snapUpMax in a single tick.
  if (gap < rest - o.groundedSkin) {
    return {
      grounded: true,
      centerY: Math.min(restY, motion.centerY + o.snapUpMax),
      vy: 0,
      phase: 'landed',
      rest,
    };
  }

  // Close enough to follow the floor: this is the ramp/stair descent path.
  //
  // The band carries an extra allowance for the rest offset SHRINKING, because
  // an ellipsoid rests higher above a slope than above flat ground (1.25 vs
  // 1.0 at 26.6 degrees). Walking off the foot of a ramp therefore settles by a
  // quarter unit through no fault of the floor, and without this term that
  // settle reads as stepping off a ledge - one frame of spurious free-fall
  // right where every visitor leaves every ramp.
  const previousRest = Number.isFinite(motion.previousRest) ? motion.previousRest : rest;
  const settle = Math.max(0, previousRest - rest);
  if (gap <= rest + snapDownLimit(motion.horizontalStep, o) + settle) {
    return { grounded: true, centerY: restY, vy: 0, phase: 'snapped', rest };
  }

  // Genuinely airborne over a known walkable surface - integrate, but land on
  // it rather than tunnelling through when this tick would carry us past.
  const falling = fall('falling');
  if (falling.centerY <= restY) {
    return { grounded: true, centerY: restY, vy: 0, phase: 'landed', rest };
  }
  return falling;
}
